import React, { useEffect, useState } from 'react';
import { useSearchParams, Link } from 'react-router-dom';
import useAuthGuard from '../hooks/useAuthGuard';
import Layout from '../components/Layout';
import StoreMap from '../components/StoreMap';
import { API_BASE_URL } from '../config';
import apiFetch from '../utils/apiFetch';
import Pagination from '../components/Pagination';

const STATUS_LABELS = {
  '': '全て',
  pending: '申請中',
  active: '公開中',
  archived: 'アーカイブ',
};

const PREFECTURES = [
  '', '北海道', '青森県', '岩手県', '宮城県', '秋田県', '山形県', '福島県',
  '茨城県', '栃木県', '群馬県', '埼玉県', '千葉県', '東京都', '神奈川県',
  '新潟県', '富山県', '石川県', '福井県', '山梨県', '長野県',
  '岐阜県', '静岡県', '愛知県', '三重県',
  '滋賀県', '京都府', '大阪府', '兵庫県', '奈良県', '和歌山県',
  '鳥取県', '島根県', '岡山県', '広島県', '山口県',
  '徳島県', '香川県', '愛媛県', '高知県',
  '福岡県', '佐賀県', '長崎県', '熊本県', '大分県', '宮崎県', '鹿児島県', '沖縄県',
];

const STORE_TYPE_OPTIONS = [
  { value: '', label: '店舗タイプ' },
  { value: 'dedicated', label: '専門店' },
  { value: 'arcade', label: 'ゲームセンター' },
  { value: 'shopping_mall', label: 'ショッピングモール' },
  { value: 'supermarket', label: 'スーパー' },
  { value: 'other', label: 'その他' },
];

export default function StoreList() {
  useAuthGuard();

  const [sp, setSp] = useSearchParams();
  const initStatus = sp.get('status') || '';
  const initQ = sp.get('q') || '';
  const initPref = sp.get('prefecture') || '';
  const initStoreType = sp.get('store_type') || '';

  const [status, setStatus] = useState(initStatus);
  const [keyword, setKeyword] = useState(initQ);
  const [queryKeyword, setQueryKeyword] = useState(initQ);
  const [prefecture, setPrefecture] = useState(initPref);
  const [storeType, setStoreType] = useState(initStoreType);
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [cursor, setCursor] = useState('');
  const [nextCursor, setNextCursor] = useState('');
  const [hasMore, setHasMore] = useState(false);
  const [total, setTotal] = useState(null);
  const [cursorHistory, setCursorHistory] = useState([]);

  const [viewMode, setViewMode] = useState(sp.get('view') || 'list');

  const [verifyTarget, setVerifyTarget] = useState(null);
  const [verifyNotes, setVerifyNotes] = useState('');
  const [verifying, setVerifying] = useState(false);
  const [actionLoading, setActionLoading] = useState('');
  const [actionResult, setActionResult] = useState(null);

  useEffect(() => {
    const p = new URLSearchParams();
    if (status) p.set('status', status);
    if (queryKeyword) p.set('q', queryKeyword);
    if (prefecture) p.set('prefecture', prefecture);
    if (storeType) p.set('store_type', storeType);
    if (viewMode !== 'list') p.set('view', viewMode);
    setSp(p, { replace: true });
  }, [status, queryKeyword, prefecture, storeType, viewMode, setSp]);

  useEffect(() => {
    const ac = new AbortController();
    (async () => {
      try {
        setLoading(true);
        setError('');
        const url = new URL(`${API_BASE_URL}/admin/stores`);
        url.searchParams.set('limit', '20');
        if (cursor) url.searchParams.set('cursor', cursor);
        if (status) url.searchParams.set('status', status);
        if (queryKeyword.trim()) url.searchParams.set('q', queryKeyword.trim());
        if (prefecture) url.searchParams.set('prefecture', prefecture);
        if (storeType) url.searchParams.set('store_type', storeType);

        const res = await apiFetch(url.toString(), { signal: ac.signal });
        if (!res.ok) throw new Error(`HTTP ${res.status}`);
        const data = await res.json();

        const list = data.data?.items || data.items || data.data || [];
        setItems(Array.isArray(list) ? list : []);
        setNextCursor(data.meta?.cursor || '');
        setHasMore(data.data?.has_more ?? data.has_more ?? data.meta?.hasMore ?? false);
        setTotal(data.meta?.total ?? null);
      } catch (e) {
        if (e.name !== 'AbortError') setError(e.message || 'Network Error');
      } finally {
        setLoading(false);
      }
    })();
    return () => ac.abort();
  }, [cursor, status, queryKeyword, prefecture, storeType]);

  const handleStatusChange = (newStatus) => {
    setStatus(newStatus);
    setCursor('');
    setCursorHistory([]);
  };

  const handleSearch = (e) => {
    e.preventDefault();
    setCursor('');
    setCursorHistory([]);
    setQueryKeyword(keyword.trim());
  };

  const handleClear = () => {
    setKeyword('');
    setQueryKeyword('');
    setPrefecture('');
    setStoreType('');
    setCursor('');
    setCursorHistory([]);
  };

  const handleFilterChange = (setter) => (e) => {
    setter(e.target.value);
    setCursor('');
    setCursorHistory([]);
  };

  const handleNext = () => {
    if (nextCursor) {
      setCursorHistory((prev) => [...prev, cursor]);
      setCursor(nextCursor);
    }
  };

  const handlePrev = () => {
    setCursorHistory((prev) => {
      const next = [...prev];
      const prevCursor = next.pop();
      setCursor(prevCursor || '');
      return next;
    });
  };

  const openVerifyModal = (store) => {
    setVerifyTarget(store);
    setVerifyNotes('');
  };

  const handleVerify = async (verified) => {
    if (!verifyTarget) return;
    setVerifying(true);
    try {
      const res = await apiFetch(`${API_BASE_URL}/admin/stores/${verifyTarget.id}/verify`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ verified, notes: verifyNotes }),
      });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);

      setItems((prev) =>
        prev.map((s) =>
          s.id === verifyTarget.id
            ? { ...s, owner_verified: verified, status: verified ? 'active' : s.status }
            : s
        )
      );
      setVerifyTarget(null);
    } catch (e) {
      alert('認証エラー: ' + e.message);
    } finally {
      setVerifying(false);
    }
  };

  const fmtDate = (s) => {
    if (!s) return '-';
    const d = new Date(s);
    if (isNaN(d)) return s;
    return new Intl.DateTimeFormat('ja-JP', {
      timeZone: 'Asia/Tokyo',
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
    }).format(d);
  };

  const statusBadge = (s) => {
    const map = { active: 'success', pending: 'warning', archived: 'secondary' };
    return map[s] || 'secondary';
  };

  const handleDeleteStore = async (store) => {
    if (!window.confirm(`「${store.name}」を削除しますか？`)) return;
    try {
      const res = await apiFetch(`${API_BASE_URL}/admin/stores/${store.id}`, { method: 'DELETE' });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      setItems((prev) => prev.filter((s) => s.id !== store.id));
      setTotal((prev) => (prev != null ? prev - 1 : prev));
    } catch (e) {
      alert('削除エラー: ' + e.message);
    }
  };

  const handleCleanupNoise = async () => {
    if (!window.confirm('ゲーム関連でない店舗データ（美容室・クリニック等）を一括削除しますか？')) return;
    setActionLoading('cleanup');
    setActionResult(null);
    try {
      const res = await apiFetch(`${API_BASE_URL}/admin/stores/cleanup-noise`, { method: 'DELETE' });
      const data = await res.json();
      setActionResult({ type: 'success', msg: `${data.data?.deleted ?? 0} 件のノイズデータを削除しました` });
      setCursor('');
      setCursorHistory([]);
    } catch (e) {
      setActionResult({ type: 'danger', msg: e.message });
    } finally {
      setActionLoading('');
    }
  };

  const hasActiveFilters = queryKeyword || prefecture || storeType;

  return (
    <Layout>
      <div className="d-flex justify-content-between align-items-start mb-4">
        <div>
          <h2 className="page-title">店舗管理</h2>
          <p className="page-subtitle">店舗の一覧・検索・認証</p>
        </div>
        <button
          className="btn btn-outline-warning btn-sm"
          onClick={handleCleanupNoise}
          disabled={!!actionLoading}
          style={{ fontSize: '0.75rem' }}
        >
          {actionLoading === 'cleanup' ? 'クリーンアップ中...' : 'ノイズデータ削除'}
        </button>
      </div>

      {actionResult && (
        <div className={`alert alert-${actionResult.type} alert-dismissible fade show`} role="alert">
          {actionResult.msg}
          <button type="button" className="btn-close" onClick={() => setActionResult(null)} />
        </div>
      )}

      {/* 検索・フィルタ */}
      <div className="card mb-3">
        <div className="card-body py-2">
          <form onSubmit={handleSearch} className="d-flex align-items-center gap-2 flex-wrap">
            <div className="search-box" style={{ minWidth: '180px', maxWidth: '260px', flex: '1' }}>
              <i className="search-icon" data-feather="search" style={{ width: 14, height: 14 }}></i>
              <input
                type="search"
                className="form-control search-input"
                placeholder="店舗名・住所で検索"
                value={keyword}
                onChange={(e) => setKeyword(e.target.value)}
              />
            </div>
            <input
              type="text"
              className="form-control"
              placeholder="都道府県"
              value={prefecture}
              onChange={handleFilterChange(setPrefecture)}
              style={{ maxWidth: '140px', fontSize: '0.8125rem', height: '2.25rem' }}
              list="prefecture-list"
            />
            <datalist id="prefecture-list">
              {PREFECTURES.filter(Boolean).map((p) => (
                <option key={p} value={p} />
              ))}
            </datalist>
            <select
              className="form-select"
              value={storeType}
              onChange={handleFilterChange(setStoreType)}
              style={{ maxWidth: '160px', fontSize: '0.8125rem', height: '2.25rem' }}
            >
              {STORE_TYPE_OPTIONS.map((o) => (
                <option key={o.value} value={o.value}>{o.label}</option>
              ))}
            </select>
            <button type="submit" className="btn btn-phoenix-primary btn-sm">検索</button>
            {hasActiveFilters && (
              <button type="button" className="btn btn-phoenix-secondary btn-sm" onClick={handleClear}>
                クリア
              </button>
            )}
          </form>
        </div>
      </div>

      {/* ビュー切替タブ */}
      <ul className="nav nav-pills mb-3">
        <li className="nav-item">
          <button
            className={`nav-link${viewMode === 'list' ? ' active' : ''}`}
            onClick={() => setViewMode('list')}
            style={{ fontSize: '0.8125rem', padding: '0.375rem 1rem' }}
          >
            リスト
          </button>
        </li>
        <li className="nav-item">
          <button
            className={`nav-link${viewMode === 'map' ? ' active' : ''}`}
            onClick={() => setViewMode('map')}
            style={{ fontSize: '0.8125rem', padding: '0.375rem 1rem' }}
          >
            マップ
          </button>
        </li>
      </ul>

      <ul className="nav nav-phoenix-tabs mb-3">
        {Object.entries(STATUS_LABELS).map(([key, label]) => (
          <li className="nav-item" key={key}>
            <button
              className={`nav-link ${status === key ? 'active' : ''}`}
              onClick={() => handleStatusChange(key)}
            >
              {label}
            </button>
          </li>
        ))}
      </ul>

      {viewMode === 'map' ? (
        <StoreMap
          status={status}
          queryKeyword={queryKeyword}
          prefecture={prefecture}
          storeType={storeType}
        />
      ) : (
      <div className="card">
        <div className="table-responsive">
          <table className="table table-hover mb-0">
            <thead className="table-light">
              <tr>
                <th>店舗名</th>
                <th>住所</th>
                <th>都道府県</th>
                <th>ステータス</th>
                <th>認証</th>
                <th>登録日</th>
                <th>操作</th>
              </tr>
            </thead>
            <tbody>
              {loading && (
                <tr>
                  <td colSpan={7} className="text-center py-4">読み込み中...</td>
                </tr>
              )}
              {!loading && error && (
                <tr>
                  <td colSpan={7} className="text-danger py-4">{error}</td>
                </tr>
              )}
              {!loading && !error && items.length === 0 && (
                <tr>
                  <td colSpan={7} className="text-center py-4" style={{ color: '#8a94ad' }}>
                    {hasActiveFilters || status ? '条件に一致する店舗がありません' : 'データがありません'}
                  </td>
                </tr>
              )}
              {!loading &&
                !error &&
                items.map((store) => (
                  <tr key={store.id}>
                    <td className="fw-medium">
                      <Link to={`/stores/${store.id}`} className="text-decoration-none">
                        {store.name || '-'}
                      </Link>
                    </td>
                    <td className="small">{store.address || '-'}</td>
                    <td className="small">{store.prefecture || '-'}</td>
                    <td>
                      <span className={`badge bg-${statusBadge(store.status)}`}>
                        {STATUS_LABELS[store.status] || store.status || '-'}
                      </span>
                    </td>
                    <td>
                      {store.owner_verified ? (
                        <span className="badge bg-success">認証済</span>
                      ) : (
                        <span className="badge bg-warning text-dark">未認証</span>
                      )}
                    </td>
                    <td className="text-nowrap">{fmtDate(store.created_at)}</td>
                    <td>
                      <div className="d-flex gap-1">
                        {!store.owner_verified && (
                          <button
                            className="btn btn-sm btn-outline-primary text-nowrap"
                            onClick={() => openVerifyModal(store)}
                            style={{ fontSize: '0.75rem' }}
                          >
                            認証
                          </button>
                        )}
                        <button
                          className="btn btn-sm btn-outline-danger text-nowrap"
                          onClick={() => handleDeleteStore(store)}
                          style={{ fontSize: '0.75rem' }}
                        >
                          削除
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
            </tbody>
          </table>
        </div>
        <div className="card-footer bg-white">
          <Pagination
            hasMore={hasMore}
            hasPrev={cursorHistory.length > 0}
            onNext={handleNext}
            onPrev={handlePrev}
            loading={loading}
            total={total}
            page={cursorHistory.length + 1}
            pageSize={20}
            itemCount={items.length}
          />
        </div>
      </div>
      )}

      {verifyTarget && (
        <div
          className="modal d-block"
          tabIndex={-1}
          style={{ backgroundColor: 'rgba(0,0,0,0.5)' }}
        >
          <div className="modal-dialog">
            <div className="modal-content">
              <div className="modal-header">
                <h5 className="modal-title" style={{ fontSize: '1rem' }}>店舗認証</h5>
                <button type="button" className="btn-close" onClick={() => setVerifyTarget(null)}></button>
              </div>
              <div className="modal-body">
                <p style={{ fontSize: '0.8125rem' }}>
                  店舗: <strong>{verifyTarget.name}</strong>
                </p>
                <div className="mb-3">
                  <label className="form-label" style={{ fontSize: '0.8125rem' }}>メモ</label>
                  <textarea
                    className="form-control"
                    rows={3}
                    value={verifyNotes}
                    onChange={(e) => setVerifyNotes(e.target.value)}
                    placeholder="（任意）"
                    style={{ fontSize: '0.8125rem' }}
                  />
                </div>
              </div>
              <div className="modal-footer">
                <button className="btn btn-phoenix-secondary btn-sm" onClick={() => setVerifyTarget(null)} disabled={verifying}>
                  キャンセル
                </button>
                <button className="btn btn-danger btn-sm" onClick={() => handleVerify(false)} disabled={verifying}>
                  却下
                </button>
                <button className="btn btn-success btn-sm" onClick={() => handleVerify(true)} disabled={verifying}>
                  {verifying ? '処理中...' : '承認'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </Layout>
  );
}
